import { useEffect, useRef } from "react";
import { useEditorStore } from "@/state/useEditorStore";
import { useMapStore } from "@/state/useMapStore";

/**
 * Keeps the latest store values in a ref so event handlers never go stale.
 * Subscribes to both map and editor stores and syncs on every change.
 */
export function useStoreRefs() {
  const latestRef = useRef({
    mapState: useMapStore.getState().mapState!,
    selectedTileSetId: useEditorStore.getState().selectedTileSetId,
    selectedBrushEntry: useEditorStore.getState().selectedBrushEntry,
    selectedDecorationId: useEditorStore.getState().selectedDecorationId,
    selectedDecorationPaintSetId: useEditorStore.getState().selectedDecorationPaintSetId,
    selectedDecorationBrushEntry: useEditorStore.getState().selectedDecorationBrushEntry,
    decorationMode: useEditorStore.getState().decorationMode,
    decorationScatterRadius: useEditorStore.getState().decorationScatterRadius,
    decorationScatterDensity: useEditorStore.getState().decorationScatterDensity,
    activeTool: useEditorStore.getState().activeTool,
    selectBrush: useEditorStore.getState().selectBrush,
    selectDecoration: useEditorStore.getState().selectDecoration,
    setSelectedBrushEntry: useEditorStore.getState().setSelectedBrushEntry,
    deselectBrush: useEditorStore.getState().deselectBrush,
    setActiveTool: useEditorStore.getState().setActiveTool,
    setDecorationMode: useEditorStore.getState().setDecorationMode,
    paintHexes: useMapStore.getState().paintHexes,
    applyMapChanges: useMapStore.getState().applyMapChanges,
    setParty: useMapStore.getState().setParty,
    pushHistory: useEditorStore.getState().pushHistory,
  });

  useEffect(() => {
    const unsubMap = useMapStore.subscribe((s) => {
      if (s.mapState) latestRef.current.mapState = s.mapState;
      latestRef.current.paintHexes = s.paintHexes;
      latestRef.current.applyMapChanges = s.applyMapChanges;
      latestRef.current.setParty = s.setParty;
    });
    const unsubEditor = useEditorStore.subscribe((s) => {
      latestRef.current.selectedTileSetId = s.selectedTileSetId;
      latestRef.current.selectedBrushEntry = s.selectedBrushEntry;
      latestRef.current.selectedDecorationId = s.selectedDecorationId;
      latestRef.current.selectedDecorationPaintSetId = s.selectedDecorationPaintSetId;
      latestRef.current.selectedDecorationBrushEntry = s.selectedDecorationBrushEntry;
      latestRef.current.decorationMode = s.decorationMode;
      latestRef.current.decorationScatterRadius = s.decorationScatterRadius;
      latestRef.current.decorationScatterDensity = s.decorationScatterDensity;
      latestRef.current.activeTool = s.activeTool;
      latestRef.current.selectBrush = s.selectBrush;
      latestRef.current.selectDecoration = s.selectDecoration;
      latestRef.current.setSelectedBrushEntry = s.setSelectedBrushEntry;
      latestRef.current.deselectBrush = s.deselectBrush;
      latestRef.current.setActiveTool = s.setActiveTool;
      latestRef.current.setDecorationMode = s.setDecorationMode;
      latestRef.current.pushHistory = s.pushHistory;
    });
    return () => {
      unsubMap();
      unsubEditor();
    };
  }, []);

  return latestRef;
}
